import * as Sentry from '@sentry/browser'
import { webAuth } from '@/auth/auth0'
import { AUTH_STORAGE_KEY } from '@/auth/constants'
import type { FindexAuthStorageData } from '@/auth/types'
import { FindexClient } from '@/integrations/findex'
import { BrowserStorage } from '@/stores/browser'
import { useUserStore } from '@/stores/user'
import { resetSharedCaches } from '@/utils/sharedCacheReset'
import type { FindexUser } from '@/types/models/user'
import { useMouseInActivity } from '@/utils/hooks/useMouseInActivity'
import { pushAutoLogoutBanner } from '@/modules/auth/banners/autoLogoutBanner'
import { setTabNotification, clearTabNotification } from '@/utils/tabNotification'
import { sleep } from '@/utils/time'
import { EUserRole, userHasRole } from 'findef'
import { computed, reactive, type Ref, type ComputedRef } from 'vue'
const AUTO_LOGOUT_SECONDS = 60 * 30
//const WEBSOCKET_URL = env.VITE_APP_WEBSOCKET_URL

type SessionState = {
  token?: string
  findexSessionId?: string
  user?: FindexUser
  userDidLoad: boolean
  loading: boolean
  microTaskExecuted: boolean
  initialized: boolean
}

const state = reactive<SessionState>({
  token: undefined,
  findexSessionId: undefined,
  user: undefined,
  userDidLoad: false,
  initialized: false,
  loading: false,
  microTaskExecuted: false
})

export type UseSesssion = {
  logout: () => Promise<void>
  setToken: (token: string, findexSessionId?: string) => Promise<void>
  kill: () => void
  state: SessionState
  isAlive: Ref<boolean>
  isSuperAdmin: ComputedRef<boolean>
  patchLocalUser: (fn: (old: FindexUser) => FindexUser) => void;

}

export const useSession = (): UseSesssion => {
  const userStore = useUserStore()

  const kill = () => {
    // Reset the in-memory state first so the user is logically signed out
    // even if a downstream side-effect throws (e.g. BrowserStorage on Safari
    // private mode, Sentry on a degraded transport). The caller (logout())
    // can then still reach webAuth.logout() after this returns.
    state.token = undefined
    state.user = undefined
    state.loading = false
    state.microTaskExecuted = false
    state.userDidLoad = false
    state.findexSessionId = undefined
    try {
      Sentry.setUser(null)
    } catch (e) {
      console.warn('[useSession] Sentry.setUser threw during kill', e)
    }
    try {
      BrowserStorage.removeItem(AUTH_STORAGE_KEY)
    } catch (e) {
      Sentry.captureException(e, { tags: { component: 'useSession', action: 'kill_remove_storage' } })
    }
    // Drop singleton caches owned by shared composables so a logout +
    // re-login as a different account never shows the previous user's data.
    // resetSharedCaches() already swallows callback failures internally.
    resetSharedCaches()
  }
  const logout = async () => {
    try {
      await FindexClient.deleteSession()
    } catch (e) {
      console.error(e)
    }

    kill()

    webAuth.logout({
      returnTo: `${window.location.origin}/auth/login`
    })
  }

  const setUser = async (user: FindexUser): Promise<boolean> => {
    state.user = user
    userStore.setUser(user)
    Sentry.setUser({ id: user._id })

    if (!state.userDidLoad) {
      state.userDidLoad = true

      const [details] = await FindexClient.getUserDetails()
      if (!details) return false
      if (details.emailVerified) return false

      await FindexClient.sendEmailVerificationCode()
    }

    return true
  }

  const patchLocalUser = (fn: (old: FindexUser) => FindexUser) => {
    const old = state.user;
    if (!old) return;
    state.user = fn(old);
  }

  const setToken = async (token: string, findexSessionId?: string) => {
    if (state.token === token && !!state.user && !!state.findexSessionId) return
    if (!token) return

    state.token = token
    state.loading = true

    const [user, res] = await FindexClient.whoami(token)
    if (!user) {
      state.token = undefined
      state.loading = false
      return
    }

    if (res && res.response) {
      findexSessionId = res.response.headers.get('X-findexSessionId') || findexSessionId
    }

    state.findexSessionId = findexSessionId

    BrowserStorage.setItem<FindexAuthStorageData>(AUTH_STORAGE_KEY, {
      token: token,
      findexSessionId: findexSessionId,
      user: '',
      timestamp: new Date()
    })

    await setUser(user)

    if (!user.emailVerified && !window.location.href.includes('verifyEmail')) {
      await navigateTo('/auth/verifyEmail')
    }

    state.loading = false
  }

  const isAlive = computed(() => !!state.user && !!state.user.email && !!state.token)

  const init = async () => {
    state.loading = true
    await sleep(60)
    const cookie = BrowserStorage.getItem<FindexAuthStorageData>(AUTH_STORAGE_KEY)
    if (cookie && cookie.token) {
      await setToken(cookie.token)
    }
    state.loading = false
  }

  if (!state.initialized) {
    queueMicrotask(async () => {
      await init()
      state.microTaskExecuted = true
    })
    state.initialized = true
  }

  const isSuperAdmin = computed(() => {
    if (state.loading) return false
    const user = state.user
    if (!user) return false
    return userHasRole(user, EUserRole.SUPER_ADMIN)
  })

  return { setToken, isAlive, kill, state, logout, isSuperAdmin, patchLocalUser }
}

export const getSessionToken = () => state.token
export const getFindexSessionId = (): string | undefined => {
  if (state.findexSessionId) return state.findexSessionId;
  const storage = BrowserStorage.getItem<{ findexSessionId: string }>(AUTH_STORAGE_KEY);
  if (storage) {
    const sessionId = storage['findexSessionId'];
    if (sessionId) return sessionId;
  }
}

export const checkExpiredSession = async (session?: UseSesssion) => {
  session = session || useSession()
  const user = session.state.user
  const token = session.state.token

  if (!user || !token) return

  const [serverSession] = await FindexClient.getUserSession()

  if (!!user.lastLogin && serverSession && serverSession.expired === true && !session.state.loading) {
    //await pushSessionExpiredModal();

    try {
      console.log(`Session has expired, signing out.`)
      session.kill()
      return
    } catch (e) {
      console.error(e)
    }
  }
}

const AUTO_LOGOUT_WARNING_SECONDS = 60

if (import.meta.env.mode !== 'dev' && window?.location?.hostname !== 'localhost') {
  useMouseInActivity({
    thresholdSeconds: AUTO_LOGOUT_SECONDS - AUTO_LOGOUT_WARNING_SECONDS,
    callback: (rewind) => {
      const session = useSession()
      if (!session.state.user || !session.isAlive.value) return

      setTabNotification('You will be logged out soon')

      pushAutoLogoutBanner({
        durationSeconds:  AUTO_LOGOUT_WARNING_SECONDS,
        onStayLoggedIn: () => {
          clearTabNotification()
          rewind()
        },
        onTimeout: async () => {
          clearTabNotification()
          await session.logout()
        }
      })
    }
  })
}

export const useAuthSession = useSession;
